import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react'
import {getConfig} from './config'
import {LatestOffersRequest, OfferRequest, ServiceOfferRequest} from '../types'
import {formatISODate, toLocalDateTime} from '../services/dateUtils'
import {RootState} from '../store/store'
import {selectSessionId} from '../store/slices/sessionSlice'

const {ibeApiUrl, ibeConfigCached} = getConfig()

export const ibeApi = createApi({
  reducerPath: 'ibeApi',
  baseQuery: fetchBaseQuery({
    baseUrl: ibeApiUrl,
    prepareHeaders: (headers, {getState}) => {
      // adds ibe session ID to headers if available
      const state = getState() as RootState
      const sessionId = selectSessionId(state)
      if (sessionId) {
        headers.set('x-ibe-session-id', sessionId)
      }
      return headers
    }
  }),
  tagTypes: [
    'BookingConfirmation',
    'Config',
    'LatestOffers',
    'Offers',
    'ServiceOffers'
  ],
  endpoints: builder => ({
    getConfig: builder.query({
      query() {
        return `/config?cache=${ibeConfigCached}`
      },
      providesTags: ['Config'],
      keepUnusedDataFor: 120000
    }),
    getLatestOffers: builder.query({
      query(params: LatestOffersRequest) {
        return {
          url: `/rate-plan-offers`,
          method: 'POST',
          body: params
        }
      },
      providesTags: ['LatestOffers']
    }),
    getBookingConfirmation: builder.query({
      query(paymentSessionId: string) {
        return {
          url: `/booking-confirmation?paymentSessionId=${paymentSessionId}`,
          method: 'GET'
        }
      },
      providesTags: ['BookingConfirmation']
    }),
    searchOffers: builder.query({
      query(params: OfferRequest) {
        const dateRange = params.dateRange
        return {
          url: `/offers`,
          method: 'POST',
          body: {
            ...params,
            dateRange: {
              ...dateRange,
              start: formatISODate(toLocalDateTime(dateRange.start)),
              end: formatISODate(toLocalDateTime(dateRange.end))
            }
          }
        }
      },
      providesTags: ['Offers']
    }),
    searchServiceOffers: builder.query({
      query(params: ServiceOfferRequest) {
        const dateRange = params.dateRange
        return {
          url: `/service-offers`,
          method: 'POST',
          body: {
            ...params,
            dateRange: {
              ...dateRange,
              start: formatISODate(toLocalDateTime(dateRange.start)),
              end: formatISODate(toLocalDateTime(dateRange.end))
            }
          }
        }
      },
      providesTags: ['ServiceOffers']
    })
  })
})

export const {
  useGetConfigQuery,
  useSearchOffersQuery,
  useLazyGetBookingConfirmationQuery,
  useLazyGetLatestOffersQuery,
  useLazySearchOffersQuery,
  useSearchServiceOffersQuery
} = ibeApi
